// Home Key — curriculum map.
// Drives the hub tile grid: every lesson appears from day one, locked tiles
// show their teaser + what the track gains. Unlock order is strictly linear
// across the flattened lesson list.

export const UNITS = [
  {
    id: "home",
    title: "Home",
    tagline: "One note, and the gravity around it",
    lessons: [
      {
        id: "home-note",
        title: "The Note That Feels Like Home",
        teaser: "Every song is a trip away from one note.",
        gain: "its first sound",
      },
      {
        id: "seven-neighbors",
        title: "The Seven Neighbors",
        teaser: "Home is a neighborhood. Meet the other six.",
        gain: "a palette",
      },
      {
        id: "leaving-returning",
        title: "Leaving and Returning",
        teaser: "Question, answer. Tension, release. Your first melody.",
        gain: "its seed motif",
      },
    ],
  },
  {
    id: "distances",
    title: "Distances",
    tagline: "Intervals — the atoms of tension",
    lessons: [
      {
        id: "steps-and-leaps",
        title: "Steps & Leaps",
        teaser: "There are only two ways to move. They feel completely different.",
        gain: "smoother moves",
      },
      {
        id: "colors-of-distance",
        title: "The Colors of Distance",
        teaser: "Every gap between two notes has its own flavor.",
        gain: "an interval palette",
      },
      {
        id: "two-at-once",
        title: "Two at Once",
        teaser: "Play two notes together and something brand-new appears.",
        gain: "its first harmony",
      },
    ],
  },
  {
    id: "melody-shapes",
    title: "Melody Shapes",
    tagline: "Phrases, motifs, and the art of almost-repeating",
    lessons: [
      {
        id: "contour",
        title: "Melodies Are Drawings",
        teaser: "Rise, fall, arch, dive — hear the shape before the notes.",
        gain: "a stronger line",
      },
      {
        id: "say-it-again",
        title: "Say It Again, But Different",
        teaser: "Why your favorite hooks repeat themselves — almost.",
        gain: "motif variations",
      },
      {
        id: "question-answer",
        title: "Question & Answer",
        teaser: "Two phrases that need each other.",
        gain: "a full 4-bar melody",
      },
    ],
  },
  {
    id: "chords",
    title: "Chords & Their Jobs",
    tagline: "Stacks of the scale, each with work to do",
    lessons: [
      {
        id: "stacking",
        title: "Three Notes, One Feeling",
        teaser: "Stack the scale on itself and chords appear.",
        gain: "its first chord",
      },
      {
        id: "seven-chords",
        title: "The Seven Chords of Home",
        teaser: "Your key comes with seven chords built in. Each has a mood.",
        gain: "a chord palette",
      },
      {
        id: "chord-jobs",
        title: "Home, Away, Tension",
        teaser: "Chords have jobs, not just names.",
        gain: "direction",
      },
      {
        id: "loops-journeys",
        title: "Loops & Journeys",
        teaser: "Four chords that power a thousand songs — and why.",
        gain: "its chord loop",
      },
    ],
  },
  {
    id: "melody-meets-harmony",
    title: "Melody Meets Harmony",
    tagline: "Where lines and chords agree to disagree",
    lessons: [
      {
        id: "chord-tones",
        title: "Safe Notes, Spicy Notes",
        teaser: "Some melody notes melt into the chord. Others sparkle against it.",
        gain: "melody-chord glue",
      },
      {
        id: "targets-passing",
        title: "Targets & Passing Notes",
        teaser: "Melodies land on purpose. Learn to aim.",
        gain: "smoother lines",
      },
      {
        id: "the-rewrite",
        title: "The Rewrite",
        teaser: "Your seed motif meets your chord loop.",
        gain: "melody + chords, together",
      },
    ],
  },
  {
    id: "minor-side",
    title: "The Minor Side",
    tagline: "Same notes, new home, whole new weather",
    lessons: [
      {
        id: "new-home",
        title: "Same Notes, New Home",
        teaser: "Move home three steps down and the world turns blue.",
        gain: "a second mood",
      },
      {
        id: "minor-moves",
        title: "Minor Progressions",
        teaser: "The chords of E minor — and the doorbell you have to borrow.",
        gain: "a moodier loop",
      },
      {
        id: "mood-switch",
        title: "The Mood Switch",
        teaser: "Build your track a shadow side.",
        gain: "a B section",
      },
    ],
  },
  {
    id: "groove",
    title: "Groove & Bass",
    tagline: "Strong beats, weak beats, and the pocket between",
    lessons: [
      {
        id: "the-grid",
        title: "The Grid",
        teaser: "Time in music is a grid of strong and weak. Feel it first.",
        gain: "a pulse",
      },
      {
        id: "drums",
        title: "Kick, Snare, Hat",
        teaser: "Three sounds, infinite grooves.",
        gain: "a beat",
      },
      {
        id: "bass",
        title: "Roots in the Pocket",
        teaser: "The bass is where harmony and rhythm shake hands.",
        gain: "a bassline",
      },
    ],
  },
  {
    id: "layers",
    title: "Layers, Dynamics & Sound",
    tagline: "Arranging energy — and dressing the song",
    lessons: [
      {
        id: "roles-space",
        title: "Layers & Space",
        teaser: "Every part needs its own lane — in pitch, in rhythm, in time.",
        gain: "an arrangement",
      },
      {
        id: "builds-drops",
        title: "Builds & Drops",
        teaser: "Energy is density. Add, remove, explode.",
        gain: "dynamics",
      },
      {
        id: "dressing",
        title: "Dressing the Track",
        teaser: "Same notes, three worlds: lo-fi, synthwave, indie.",
        gain: "a style",
      },
      {
        id: "assembly",
        title: "The Assembly",
        teaser: "Put every keeper in its place. Press play on your song.",
        gain: "its final form",
      },
    ],
  },
];

export const ALL_LESSONS = UNITS.flatMap((unit) =>
  unit.lessons.map((lesson) => ({ ...lesson, unitId: unit.id }))
);

const INDEX_BY_ID = new Map(ALL_LESSONS.map((l, i) => [l.id, i]));

export const lessonIndex = (lessonId) =>
  INDEX_BY_ID.has(lessonId) ? INDEX_BY_ID.get(lessonId) : -1;

export const getLesson = (lessonId) =>
  ALL_LESSONS[lessonIndex(lessonId)] || null;

export const nextLessonId = (lessonId) => {
  const i = lessonIndex(lessonId);
  if (i < 0 || i + 1 >= ALL_LESSONS.length) return null;
  return ALL_LESSONS[i + 1].id;
};

// Linear unlock: a lesson is unlocked when every lesson before it is complete.
export const isUnlocked = (lessonId, completed = {}) => {
  const i = lessonIndex(lessonId);
  if (i < 0) return false;
  return ALL_LESSONS.slice(0, i).every((l) => completed[l.id]);
};

// The furthest unlocked, not-yet-completed lesson — the "current" tile.
export const currentLessonId = (completed = {}) => {
  const next = ALL_LESSONS.find((l) => !completed[l.id]);
  return next ? next.id : null;
};

// Which lessons actually have playable moment data yet (later units are
// visible on the map but not yet authored).
export const lessonTileState = (lessonId, completed = {}, authoredIds = []) => {
  if (completed[lessonId]) return "done";
  if (!isUnlocked(lessonId, completed)) return "locked";
  return authoredIds.includes(lessonId) ? "current" : "coming-soon";
};
